<template>
  <h2 class="mb-6">
    {{ t("components.siteSettings.integrations.title") }}
  </h2>

  <!-- PUSH -->
  <v-card
    :subtitle="t('components.siteSettings.integrations.push.subtitle')"
    elevation="3"
    class="ma-3 mb-8"
  >
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="font-weight-black">{{
            t("components.siteSettings.integrations.push.title")
          }}</span>
          <v-chip
            v-if="pushMode === Mode.VIEW"
            class="ml-3"
            :color="isPushIntegrated ? 'success' : 'error'"
            :text="
              isPushIntegrated
                ? t('components.siteSettings.integrations.push.connected')
                : t('components.siteSettings.integrations.push.notConnected')
            "
            :variant="!isPushIntegrated ? 'outlined' : undefined"
          />
        </div>
        <v-btn-nd v-if="pushMode === Mode.VIEW" variant="text" @click="pushMode = Mode.EDIT">
          <v-icon>mdi-pencil</v-icon>
        </v-btn-nd>
        <v-btn-nd v-else variant="text" @click="pushMode = Mode.VIEW">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn-nd>
      </div>
    </template>

    <v-card-text v-if="pushMode === Mode.EDIT">
      <div v-if="isPushIntegrated" class="d-flex align-center">
        <p class="me-3">
          {{ t("components.siteSettings.integrations.push.integratedWith") }}:
          <kbd>{{ push?.provider }}</kbd>
        </p>
        <v-btn
          :loading="loading"
          color="error"
          @click="
            emit('updateInternalSettings', 'connectPush', undefined);
            pushMode = Mode.VIEW;
          "
        >
          {{ t("components.siteSettings.integrations.push.removeIntegration") }}
        </v-btn>
      </div>

      <div v-else class="d-flex align-center">
        <p class="me-3">{{ t("components.siteSettings.integrations.push.integrateWith") }}:</p>
        <v-select
          v-model="pushProvider"
          hide-details
          max-width="300"
          class="me-3"
          :items="pushIntegrations"
        />
        <v-btn
          :loading="loading"
          @click="
            emit('updateInternalSettings', 'connectPush', pushToEmit);
            pushMode = Mode.VIEW;
          "
        >
          {{ t("form.save") }}
        </v-btn>
      </div>
    </v-card-text>

    <v-card-text v-else-if="isPushIntegrated">
      <p>
        {{ t("components.siteSettings.integrations.push.integratedWith") }}:
        <kbd>{{ push?.provider }}</kbd>
      </p>
    </v-card-text>
  </v-card>

  <!-- PULL -->
  <v-card
    :subtitle="t('components.siteSettings.integrations.pull.subtitle')"
    elevation="3"
    class="ma-3"
  >
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="font-weight-black">{{
            t("components.siteSettings.integrations.pull.title")
          }}</span>
          <v-chip
            v-if="pullMode === Mode.VIEW"
            class="ml-3"
            :color="isPullIntegrated ? 'success' : 'error'"
            :text="
              isPullIntegrated
                ? t('components.siteSettings.integrations.pull.connected')
                : t('components.siteSettings.integrations.pull.notConnected')
            "
            :variant="!isPullIntegrated ? 'outlined' : undefined"
          />
        </div>
        <v-btn-nd v-if="pullMode === Mode.VIEW" variant="text" @click="pullMode = Mode.EDIT">
          <v-icon>mdi-pencil</v-icon>
        </v-btn-nd>
        <v-btn-nd v-else variant="text" @click="pullMode = Mode.VIEW">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn-nd>
      </div>
    </template>

    <v-card-text v-if="pullMode === Mode.EDIT">
      <div class="d-flex align-center flex-column w-100">
        <div class="ga-2 d-flex flex-column w-100 mb-6">
          <v-checkbox
            v-model="isPullEnabledInput"
            :label="t('components.siteSettings.integrations.pull.enableLabel')"
            :hint="t('components.siteSettings.integrations.pull.enableHint')"
            persistent-hint
            inset
            color="primary"
            class="mb-4"
            hide-details
          />
          <p>{{ t("components.siteSettings.integrations.pull.providerLabel") }}:</p>
          <v-select
            v-model="pullProviderInput"
            hide-details
            density="compact"
            max-width="300"
            class="me-3"
            :items="pullIntegrations"
          />
        </div>

        <div class="d-flex w-100 flex-column ga-2">
          <Ncrv1Form
            v-if="pullProviderInput === 'ncrv1'"
            v-model:pull-integration-config="
              pullIntegrationConfigInput as CPullNCRIntegrationConfig
            "
          />
          <Zonalv1Form
            v-if="pullProviderInput === 'zonalv1'"
            v-model:pull-integration-config="
              pullIntegrationConfigInput as CPullZonalIntegrationConfig
            "
          />
        </div>

        <div class="d-flex ga-4">
          <v-btn
            v-if="isPullIntegrated"
            :loading="loading"
            color="error"
            @click="
              emit('updateInternalSettings', 'connectPull', undefined);
              pullMode = Mode.VIEW;
            "
          >
            {{ t("components.siteSettings.integrations.pull.removeIntegration") }}
          </v-btn>

          <v-btn
            :loading="loading"
            @click="
              emit('updateInternalSettings', 'connectPull', pullToEmit);
              pullMode = Mode.VIEW;
            "
          >
            {{ t("form.save") }}
          </v-btn>
        </div>
      </div>
    </v-card-text>

    <v-card-text v-else-if="isPullIntegrated" class="d-flex ga-1 flex-column">
      <v-checkbox
        v-model="isPullEnabledInput"
        :label="t('components.siteSettings.integrations.pull.isEnabled')"
        inset
        color="primary"
        disabled
        hide-details
      />
      <p class="pl-3">
        {{ t("components.siteSettings.integrations.pull.integratedWith") }}:
        <kbd>{{ pull?.provider }}</kbd>
      </p>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import {
  SiteInternalSettingsConnectPush,
  SiteInternalSettingsConnectPushProviderEnum,
  SiteInternalSettingsConnectPull,
  SiteInternalSettingsConnectPullProviderEnum,
} from "@tableyeti/merchant-service";
import {
  CPullNCRIntegrationConfig,
  CPullZonalIntegrationConfig,
  SiteInternalSettingsConnectPullIntegrationConfig,
} from "@tableyeti/merchant-service/api.ts";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Ncrv1Form from "@/components/SiteSettings/PullForms/Ncrv1Form.vue";
import Zonalv1Form from "@/components/SiteSettings/PullForms/Zonalv1Form.vue";

const { t } = useI18n();

const { push, pull } = defineProps<{
  push?: SiteInternalSettingsConnectPush;
  pull?: SiteInternalSettingsConnectPull;
  loading?: boolean;
}>();
const emit = defineEmits<{
  updateInternalSettings: [
    string,
    SiteInternalSettingsConnectPush | SiteInternalSettingsConnectPull | undefined,
  ];
}>();

enum Mode {
  VIEW = "view",
  EDIT = "edit",
}

const isPushIntegrated = computed(() => !!push);
const pushMode = ref<Mode>(Mode.VIEW);
const pushIntegrations = Object.values(SiteInternalSettingsConnectPushProviderEnum).map(
  (value) => ({
    title: value,
    value,
    props: { disabled: value !== SiteInternalSettingsConnectPushProviderEnum.PsConnect },
  }),
);
const pushProvider = ref<SiteInternalSettingsConnectPushProviderEnum | undefined>(undefined);
const pushToEmit = computed<SiteInternalSettingsConnectPush | undefined>(() => ({
  provider: pushProvider.value!,
}));

const pullMode = ref<Mode>(Mode.VIEW);
const pullToEmit = computed<SiteInternalSettingsConnectPull | undefined>(() => ({
  enabled: isPullEnabledInput.value,
  provider: pullProviderInput.value!,
  integrationConfig: pullIntegrationConfigInput?.value,
}));

const isPullIntegrated = computed(() => !!pull);
const isPullEnabledInput = ref<boolean>(!!pull?.enabled);
const pullProviderInput = ref<SiteInternalSettingsConnectPullProviderEnum | undefined>(
  pull?.provider,
);
const pullIntegrations = Object.values(SiteInternalSettingsConnectPullProviderEnum).map(
  (value) => ({
    title: value,
    value,
    props: {},
  }),
);
const pullIntegrationConfigInput = ref<
  SiteInternalSettingsConnectPullIntegrationConfig | undefined
>(pull?.integrationConfig);

watch(
  () => pullProviderInput.value,
  (newProvider) => {
    if (newProvider === SiteInternalSettingsConnectPullProviderEnum.Ncrv1) {
      const existingConfig = pull?.integrationConfig as CPullNCRIntegrationConfig;
      pullIntegrationConfigInput.value = {
        siteId: existingConfig?.siteId,
        waiterId: existingConfig?.waiterId,
        authentication: existingConfig?.authentication,
      };
    } else if (newProvider === SiteInternalSettingsConnectPullProviderEnum.Zonalv1) {
      const existingConfig = pull?.integrationConfig as CPullZonalIntegrationConfig;
      pullIntegrationConfigInput.value = {
        identityGuid: existingConfig?.identityGuid ?? "",
        iOrderBrandToken: existingConfig?.iOrderBrandToken ?? "",
        iOrderUsername: existingConfig?.iOrderUsername ?? "",
        siteId: existingConfig?.siteId ?? 0,
        salesAreaId: existingConfig?.salesAreaId ?? 0,
        iOrderPassword: existingConfig?.iOrderPassword ?? "",
        identitySecret: existingConfig?.identitySecret ?? "",
        paymentMethodIds: existingConfig?.paymentMethodIds ?? { default: 0 },
      };
    }
  },
);
</script>
